import React, { useState, useEffect } from 'react';
import pkg from '../../../package.json';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import TuneIcon from '@mui/icons-material/Tune';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VibrationIcon from '@mui/icons-material/Vibration';
import { useGame } from '../../contexts/GameContext';
import { getSyncStatusMessage } from '../../services/gameProgressSync';
import GoogleLoginComponent from '../GoogleLoginComponent';
import {
  hapticTap,
  hapticDrawerOpen,
  isHapticsEnabled,
  setHapticsEnabled as saveHapticsEnabled,
} from '../../utils/haptics';

const APP_VERSION = pkg.version;
const drawerWidth = 240;

const VIEW_TITLES = {
  home: 'Every Voice Can Sing',
  play: 'Choose a Level',
  levelGrid: 'Choose a Level',
  gameLevel: 'Level',
  gameResults: 'Results',
  progress: 'Your Progress',
  achievements: 'Achievements',
  nada: 'Nāda · Sound Meditation',
};

// Interpolates a value between `from` and `to` as `t` goes 0→1, clamped.
const AppLayout = ({ children, currentView, onNavigate, onRequestCalibration, onRequestTour }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [calibratedFrequency, setCalibratedFrequency] = useState(null);
  const [hapticsOn, setHapticsOn] = useState(() => isHapticsEnabled());

  // Nāda mode — soothing dark-green AppBar instead of bright green
  const isNada = currentView === 'nada';
  const appBarBg = isNada
    ? 'linear-gradient(135deg, #0c2d1e 0%, #1a5c38 50%, #0c2d1e 100%)'
    : '#58CC02';

  // Get sync status from GameContext
  const { syncStatus } = useGame();

  // Keep hapticsOn state in sync with external changes (e.g. other tabs)
  useEffect(() => {
    const onPrefChange = (e) => setHapticsOn(e.detail.enabled);
    window.addEventListener('hapticsPreferenceChanged', onPrefChange);
    return () => window.removeEventListener('hapticsPreferenceChanged', onPrefChange);
  }, []);

  // Check localStorage for calibrated frequency on mount and listen for changes
  useEffect(() => {
    const updateFrequency = () => {
      const savedCalibration = localStorage.getItem('userSaFrequency');
      if (savedCalibration) {
        const frequency = parseFloat(savedCalibration);
        if (!isNaN(frequency)) {
          setCalibratedFrequency(frequency);
        }
      } else {
        setCalibratedFrequency(null);
      }
    };

    // Initial load
    updateFrequency();

    // Listen for storage changes
    window.addEventListener('storage', updateFrequency);
    
    // Listen for custom event when calibration changes
    window.addEventListener('calibrationChanged', updateFrequency);

    return () => {
      window.removeEventListener('storage', updateFrequency);
      window.removeEventListener('calibrationChanged', updateFrequency);
    };
  }, []);

  // Get sync icon and color based on status
  const getSyncIcon = () => {
    switch (syncStatus.status) {
      case 'syncing':
        return <CloudSyncIcon fontSize="small" />;
      case 'synced':
        return <CloudDoneIcon fontSize="small" />;
      case 'offline':
        return <CloudOffIcon fontSize="small" />;
      case 'error':
        return <ErrorOutlineIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getSyncColor = () => {
    switch (syncStatus.status) {
      case 'syncing':
        return 'info';
      case 'synced':
        return 'success';
      case 'offline':
        return 'default';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleDrawerToggle = () => {
    hapticDrawerOpen();
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (view) => {
    hapticTap();
    onNavigate(view);
    if (isMobile) setMobileOpen(false);
  };

  const handleRecalibrate = () => {
    hapticTap();
    if (onRequestCalibration) onRequestCalibration();
    if (isMobile) setMobileOpen(false);
  };

  const handleShowTour = () => {
    hapticTap();
    if (onRequestTour) onRequestTour();
    if (isMobile) setMobileOpen(false);
  };

  const handleHapticsToggle = (e) => {
    const next = e.target.checked;
    saveHapticsEnabled(next);
    setHapticsOn(next);
  };

  const menuItems = [
    { id: 'home', label: 'Home', icon: <HomeIcon />, color: '#1976d2' },
    { id: 'nada', label: 'Nāda', icon: <SelfImprovementIcon />, color: '#8b5cf6' },
    { id: 'play', label: 'PLAY', icon: <SportsEsportsIcon />, color: '#58CC02', highlight: true },
    { id: 'progress', label: 'Progress', icon: <BarChartIcon />, color: '#ff9800' },
    { id: 'achievements', label: 'Achievements', icon: <EmojiEventsIcon />, color: '#ffd700' },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ background: appBarBg, color: 'white', paddingTop: 'calc(env(safe-area-inset-top) + 8px)', transition: 'background 0.6s ease' }}>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
          🎵 EVCS
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={currentView === item.id}
              onClick={() => handleNavigation(item.id)}
              sx={{
                mx: 1,
                borderRadius: 2,
                backgroundColor: item.highlight && currentView === item.id ? '#58CC02' : 'transparent',
                color: item.highlight && currentView === item.id ? 'white' : 'inherit',
                '&.Mui-selected': {
                  backgroundColor: item.highlight ? '#58CC02' : 'rgba(0, 0, 0, 0.08)',
                  color: item.highlight ? 'white' : 'inherit',
                  '&:hover': {
                    backgroundColor: item.highlight ? '#4DB802' : 'rgba(0, 0, 0, 0.12)',
                  },
                },
                '&:hover': {
                  backgroundColor: item.highlight ? 'rgba(88, 204, 2, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemIcon sx={{ 
                color: item.highlight && currentView === item.id ? 'white' : item.color,
                minWidth: 40 
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: item.highlight ? 'bold' : 'normal',
                  fontSize: item.highlight ? '1.1rem' : '1rem',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      
      {/* Sync Status — hide when offline (app is offline-first via localStorage) */}
      {syncStatus.status !== 'idle' && syncStatus.status !== 'offline' && (
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={syncStatus.errorMessage || getSyncStatusMessage()} arrow>
            <Chip
              icon={getSyncIcon()}
              label={getSyncStatusMessage()}
              size="small"
              color={getSyncColor()}
              variant="outlined"
              sx={{ 
                fontSize: '0.75rem',
                height: 'auto',
                py: 0.5,
              }}
            />
          </Tooltip>
        </Box>
      )}
      
      {/* Recalibrate Voice Option */}
      <List sx={{ pt: 1, pb: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleRecalibrate}
            sx={{
              mx: 1,
              borderRadius: 2,
              '&:hover': { backgroundColor: 'rgba(88, 204, 2, 0.08)' },
            }}
          >
            <ListItemIcon sx={{ color: '#58CC02', minWidth: 40 }}>
              <TuneIcon />
            </ListItemIcon>
            <ListItemText
              primary="Recalibrate Voice"
              secondary={calibratedFrequency ? `Sa: ${calibratedFrequency.toFixed(1)} Hz` : 'Not calibrated'}
              primaryTypographyProps={{ fontSize: '0.9rem' }}
              secondaryTypographyProps={{
                fontSize: '0.75rem',
                color: calibratedFrequency ? '#58CC02' : 'text.secondary',
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleShowTour}
            sx={{
              mx: 1,
              borderRadius: 2,
              '&:hover': { backgroundColor: 'rgba(103, 126, 234, 0.08)' },
            }}
          >
            <ListItemIcon sx={{ color: '#667eea', minWidth: 40 }}>
              <AutoStoriesIcon />
            </ListItemIcon>
            <ListItemText
              primary="Show App Tour"
              secondary="Replay the intro walkthrough"
              primaryTypographyProps={{ fontSize: '0.9rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItemButton>
        </ListItem>

        {/* Haptics preference toggle */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleHapticsToggle({ target: { checked: !hapticsOn } })}
            sx={{
              mx: 1,
              borderRadius: 2,
              '&:hover': { backgroundColor: 'rgba(233, 30, 99, 0.06)' },
            }}
          >
            <ListItemIcon sx={{ color: '#e91e63', minWidth: 40 }}>
              <VibrationIcon />
            </ListItemIcon>
            <ListItemText
              primary="Haptic Feedback"
              secondary={hapticsOn ? 'On' : 'Off'}
              primaryTypographyProps={{ fontSize: '0.9rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
            <Switch
              checked={hapticsOn}
              onChange={handleHapticsToggle}
              onClick={(e) => e.stopPropagation()}
              size="small"
              color="secondary"
            />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          v{APP_VERSION}
        </Typography>
      </Box>
    </Box>
  );

  // Background color — outer shell fills the bottom safe-area margin on iOS;
  // matching it to the view avoids the 'white strip at bottom' on iPhones.
  // Must match the <main> per-view bg so the safe-area strip at the bottom is the same colour.
  const shellBg = isNada ? '#000008' : currentView === 'home' ? '#58CC02' : '#f5f5f5';

  return (
    <Box sx={{
      display: 'flex',
      // Pin the app shell to the exact viewport area — prevents page-level
      // scrolling and the iOS rubber-band bounce that shifts the whole layout.
      // The <main> element below handles internal scrolling only.
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      backgroundColor: shellBg,
    }}>
      {/* Mobile AppBar — hidden in landscape during gameplay (overlay takes full screen) */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            width: '100%',
            background: appBarBg,
            transition: 'background 0.6s ease',
            paddingTop: 'env(safe-area-inset-top)',
            '@media (orientation: landscape)': {
              display: currentView === 'gameLevel' ? 'none' : undefined,
            },
          }}
        >
          <Toolbar sx={{ minHeight: 44, alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1.5 }}
            >
              <MenuIcon sx={{ fontSize: '1.4rem' }} />
            </IconButton>

            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  lineHeight: 1,
                  color: 'white',
                }}
              >
                {currentView === 'home'
                  ? '🎵 EVCS · Every Voice Can Sing'
                  : (VIEW_TITLES[currentView] || 'EVCS')}
              </Typography>
            </Box>

            <GoogleLoginComponent />
          </Toolbar>
        </AppBar>
      )}

      {/* Side Panel - Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              border: 'none',
              boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area — fixed-frame, only this element scrolls (not the page) */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          height: '100%',
          overflowY: 'auto',
          overscrollBehavior: 'none',
          WebkitOverflowScrolling: 'touch',
          // Per-view content background — must equal shellBg so the
          // safe-area padding zone at the bottom never shows a different colour.
          backgroundColor: currentView === 'nada' ? '#000008' : '#f5f5f5',
          // Push content below the fixed AppBar + status-bar safe area.
          // transition keeps the layout smooth during orientation changes.
          // In landscape during gameplay the AppBar is hidden, so no top padding needed.
          pt: { xs: 'calc(44px + env(safe-area-inset-top))', md: 0 },
          '@media (orientation: landscape)': {
            pt: currentView === 'gameLevel' ? 0 : undefined,
          },
          transition: 'padding-top 0.25s ease',
          // Reserve space above the home-indicator bar.
          pb: { xs: 'env(safe-area-inset-bottom, 0px)', md: 0 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AppLayout;
